import { PublicClientApplication } from "@azure/msal-browser";
import { environment } from "../environments/environment";
import { setAuthCookie } from "./authService";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
const redirectUri =
  import.meta.env.MODE === "development"
    ? "http://localhost:5173"
    : import.meta.env.MODE === "staging"
      ? "https://kodo-staging.aspsols.com/"
      : "https://kodo-develop.aspsols.com/";

const application_id = environment.msalConfig.auth.clientId;
const tenantId = environment.msalConfig.auth.tenantId;
const graphEndpoint = environment.msalConfig.graphConf.graphMeEndpoint;
const scopes = environment.msalConfig.scopes.scope;

export const msalConfig = {
  auth: {
    clientId: application_id,
    authority: "https://login.microsoftonline.com/" + tenantId,
    redirectUri: redirectUri,
  },
  cache: {
    cacheLocation: "localStorage", // This configures where your cache will be stored
    storeAuthStateInCookie: true, // Set this to "true" if you are having issues on IE11 or Edge
  },
};

const msalInstance = new PublicClientApplication(msalConfig);

async function initializeMsal() {
  try {
    await msalInstance.initialize();
  } catch (error) {
    console.error("Error initializing MSAL:", error);
  }
}

// export async function acquireAndValidateToken() {
//   await initializeMsal();

//   const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

//   if (!account) {
//     throw new Error("No account found. User may not be signed in.");
    
//   }

//   msalInstance.setActiveAccount(account);

//   const tokenRequest = {
//     scopes: ["openid", "profile", "User.Read", "offline_access"],
//     account,
//   };
//   try {
//     const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
//     const currentTime = Date.now();
//     const expirationTime = new Date(tokenResponse.expiresOn).getTime();
//     if (currentTime > expirationTime) {
//       localStorage.removeItem("u_tk");
//       throw new Error("Token found expired");
//     }
//     localStorage.setItem("session_Exp_tok", expirationTime);
//     localStorage.removeItem("u_tk");
//     localStorage.setItem("u_tk", tokenResponse.idToken);
//     setAuthCookie("u_tk", tokenResponse.idToken);
//     return { isAuthenticated: true };
//   } catch (error) {
//     console.error("Error acquiring token silently:", error);
//     return { isAuthenticated: false };
//   }
// }

export async function acquireAndValidateToken() {
  try {
    await initializeMsal();

    const account = msalInstance.getActiveAccount() || msalInstance.getAllAccounts()[0];

    if (!account) {
      console.error("No account found for token acquisition");
      return { 
        isAuthenticated: false, 
        error: "NO_ACCOUNT",
        message: "No account found. User may not be signed in."
      };
    }

    msalInstance.setActiveAccount(account);

    const tokenRequest = {
      scopes: ["openid", "profile", "User.Read", "offline_access"],
      account,
      forceRefresh: true // Fuerza la obtención de un token nuevo
    };

    try {
      const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
      
      if (!tokenResponse || !tokenResponse.idToken) {
        throw new Error("Invalid token response");
      }

      const currentTime = Date.now();
      const expirationTime = new Date(tokenResponse.expiresOn).getTime();
      
      localStorage.setItem("session_Exp_tok", expirationTime);
      localStorage.setItem("u_tk", tokenResponse.idToken);
      setAuthCookie("u_tk", tokenResponse.idToken);
      
      console.log("Token renewed successfully. Expires at:", new Date(expirationTime).toLocaleString());
      
      return { 
        isAuthenticated: true,
        expiresOn: expirationTime 
      };
      
    } catch (silentError) {
      console.error("Silent token acquisition failed:", silentError);
      
      // Intentar con login interactivo como fallback
      try {
        console.log("Attempting interactive login as fallback...");
        const interactiveResponse = await msalInstance.loginPopup({
          scopes: ["openid", "profile", "User.Read", "offline_access"],
          prompt: 'select_account' // Fuerza la selección de cuenta
        });
        
        if (interactiveResponse && interactiveResponse.idToken) {
          const expirationTime = new Date(interactiveResponse.expiresOn).getTime();
          localStorage.setItem("session_Exp_tok", expirationTime);
          localStorage.setItem("u_tk", interactiveResponse.idToken);
          setAuthCookie("u_tk", interactiveResponse.idToken);
          
          msalInstance.setActiveAccount(interactiveResponse.account);
          
          return { 
            isAuthenticated: true,
            expiresOn: expirationTime,
            source: "interactive" 
          };
        }
      } catch (interactiveError) {
        console.error("Interactive login also failed:", interactiveError);
      }
      
      return { 
        isAuthenticated: false, 
        error: "TOKEN_ACQUISITION_FAILED",
        message: "Failed to acquire token both silently and interactively"
      };
    }
    
  } catch (error) {
    console.error("Unexpected error in acquireAndValidateToken:", error);
    return { 
      isAuthenticated: false, 
      error: "UNEXPECTED_ERROR",
      message: error.message 
    };
  }
}
export async function renewToken() {
  const result = await acquireAndValidateToken();
  
  if (!result.isAuthenticated) {
    throw new Error(`Token renewal failed: ${result.error} - ${result.message}`);
  }
  
  return result;
}
/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: scopes,
  prompt: "select_account",
};

/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 */
export const graphConfig = {
  graphMeEndpoint: graphEndpoint,
};
